import { useRef, useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { Alert } from '../atoms/Alert/Alert';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { Input } from '../atoms/Input/Input';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Stack } from '../atoms/Stack/Stack';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { Section } from '../templates/Section/Section';
import { useTranslation } from '../../i18n/useTranslation';
import { ApiError } from '../../lib/apiClient';
import {
  MFA_STATUS_QUERY_KEY,
  confirmTotpSetup,
  disableTotp,
  fetchMfaStatus,
  formatSecret,
  startTotpSetup,
  type MfaStatus,
  type TotpSetup,
} from '../../services/mfaService';
import './TwoFactorSection.css';

export interface TwoFactorSectionProps {
  className?: string;
}

type Mode = 'idle' | 'setup' | 'disable';
type Notice = 'enabled' | 'disabled' | null;

// Each setup call (AssociateSoftwareToken) mints a NEW secret and invalidates the
// previous one, so a reload would silently break the QR code the user already
// scanned. Keep the secret for this tab until the setup is finished or cancelled
// (same approach as PantaLab's MfaSetupPage).
const SETUP_KEY = 'reefguide_mfa_setup';

function readCachedSetup(): TotpSetup | null {
  try {
    const raw = sessionStorage.getItem(SETUP_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TotpSetup>;
    return typeof parsed.secret === 'string' && typeof parsed.otpauthUri === 'string'
      ? { secret: parsed.secret, otpauthUri: parsed.otpauthUri }
      : null;
  } catch {
    return null;
  }
}

function cacheSetup(setup: TotpSetup | null): void {
  try {
    if (setup) sessionStorage.setItem(SETUP_KEY, JSON.stringify(setup));
    else sessionStorage.removeItem(SETUP_KEY);
  } catch {
    /* storage unavailable */
  }
}

const sixDigits = (value: string): string => value.replace(/\D/g, '').slice(0, 6);

/**
 * Settings → "Zwei-Faktor-Authentifizierung": optional second factor with an
 * authenticator app (Google Authenticator, Authy, 1Password …). Switched on by the
 * user: QR code (or setup key) → 6-digit code → active; from then on every sign-in
 * asks for a code. Switching off needs the password and a current code. A user who
 * lost the device is pointed to support — an admin can switch it off.
 */
export function TwoFactorSection({ className = '' }: TwoFactorSectionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: MFA_STATUS_QUERY_KEY, queryFn: fetchMfaStatus, retry: false });
  const cached = readCachedSetup();
  const [mode, setMode] = useState<Mode>(cached ? 'setup' : 'idle');
  const [setup, setSetup] = useState<TotpSetup | null>(cached);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState(false);
  const failureRef = useRef<HTMLDivElement>(null);

  function reset(next: Mode): void {
    setMode(next);
    setCode('');
    setPassword('');
    setCodeError(null);
    setPasswordError(null);
    setFailure(null);
  }

  function fail(err: unknown, fallbackKey: string): void {
    const errCode = err instanceof ApiError ? err.code : '';
    if (errCode === 'CODE_MISMATCH' || errCode === 'VALIDATION_ERROR') {
      setCode('');
      setCodeError(t('settings.twoFactor.errors.invalidCode'));
    } else if (errCode === 'WRONG_PASSWORD') {
      setPasswordError(t('settings.twoFactor.errors.wrongPassword'));
    } else {
      setFailure(
        t(
          errCode === 'RATE_LIMITED'
            ? 'settings.twoFactor.errors.rateLimited'
            : errCode === 'MFA_UNAVAILABLE'
              ? 'settings.twoFactor.unavailable'
              : fallbackKey,
        ),
      );
      requestAnimationFrame(() => failureRef.current?.focus());
    }
  }

  async function beginSetup(): Promise<void> {
    reset('setup');
    setNotice(null);
    setBusy(true);
    try {
      const created = await startTotpSetup();
      cacheSetup(created);
      setSetup(created);
    } catch (err: unknown) {
      setMode('idle');
      fail(err, 'settings.twoFactor.errors.setupFailed');
    } finally {
      setBusy(false);
    }
  }

  function cancel(): void {
    cacheSetup(null);
    setSetup(null);
    reset('idle');
  }

  async function onVerify(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy) return;
    if (code.length !== 6) {
      setCodeError(t('settings.twoFactor.errors.codeRequired'));
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      const updated = await confirmTotpSetup(code);
      queryClient.setQueryData<MfaStatus>(MFA_STATUS_QUERY_KEY, updated);
      cacheSetup(null);
      setSetup(null);
      reset('idle');
      setNotice('enabled');
    } catch (err: unknown) {
      fail(err, 'settings.twoFactor.errors.generic');
    } finally {
      setBusy(false);
    }
  }

  async function onDisable(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy) return;
    const missingPassword = password === '';
    const missingCode = code.length !== 6;
    setPasswordError(missingPassword ? t('settings.twoFactor.errors.passwordRequired') : null);
    setCodeError(missingCode ? t('settings.twoFactor.errors.codeRequired') : null);
    if (missingPassword || missingCode) return;
    setBusy(true);
    setFailure(null);
    try {
      const updated = await disableTotp(password, code);
      queryClient.setQueryData<MfaStatus>(MFA_STATUS_QUERY_KEY, updated);
      reset('idle');
      setNotice('disabled');
    } catch (err: unknown) {
      fail(err, 'settings.twoFactor.errors.generic');
    } finally {
      setBusy(false);
    }
  }

  const failureAlert = failure ? (
    <Alert ref={failureRef} tabIndex={-1} compact>
      {failure}
    </Alert>
  ) : null;

  let content;
  if (status.isPending) {
    content = (
      <LiveRegion busy>
        <Text size="sm" color="muted">{t('settings.twoFactor.loading')}</Text>
      </LiveRegion>
    );
  } else if (status.isError || status.data === undefined) {
    content = (
      <LoadErrorState
        compact
        titleLevel={3}
        title={t('settings.twoFactor.loadError')}
        onRetry={() => void status.refetch()}
        retryLabel={t('common.retry')}
        retrying={status.isFetching}
      />
    );
  } else if (!status.data.available) {
    content = (
      <Alert tone="info" live="off" compact>
        {t('settings.twoFactor.unavailable')}
      </Alert>
    );
  } else if (mode === 'setup') {
    content = (
      <form onSubmit={(e) => void onVerify(e)} noValidate>
        <Stack gap="4">
          <Heading level={3}>{t('settings.twoFactor.setup.title')}</Heading>
          <Text size="sm">{t('settings.twoFactor.setup.intro')}</Text>
          {setup === null ? (
            <LiveRegion busy>
              <Text size="sm" color="muted">{t('settings.twoFactor.setup.loadingSecret')}</Text>
            </LiveRegion>
          ) : (
            <div className="two-factor__setup">
              <figure className="two-factor__qr" data-testid="mfa-qr">
                <QRCodeSVG
                  value={setup.otpauthUri}
                  size={176}
                  marginSize={4}
                  role="img"
                  aria-label={t('settings.twoFactor.setup.qrLabel')}
                />
                <figcaption>
                  <Text size="sm" color="muted">{t('settings.twoFactor.setup.scanHint')}</Text>
                </figcaption>
              </figure>
              <Stack gap="2" className="two-factor__secret-block">
                <Text size="sm" weight="semibold">
                  {t('settings.twoFactor.setup.secretLabel')}
                </Text>
                <code className="two-factor__secret" data-testid="mfa-secret">
                  {formatSecret(setup.secret)}
                </code>
                <Text size="sm" color="muted">{t('settings.twoFactor.setup.secretHint')}</Text>
              </Stack>
            </div>
          )}
          <Input
            id="two-factor-setup-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            label={t('settings.twoFactor.codeLabel')}
            placeholder="123456"
            value={code}
            onChange={(e) => {
              setCode(sixDigits(e.target.value));
              setCodeError(null);
            }}
            error={codeError ?? undefined}
            disabled={busy || setup === null}
          />
          {failureAlert}
          <div className="two-factor__actions">
            <Button type="submit" variant="primary" isLoading={busy && setup !== null} disabled={busy || setup === null}>
              {t('settings.twoFactor.setup.verifyButton')}
            </Button>
            <Button type="button" variant="ghost" onClick={cancel} disabled={busy && setup !== null}>
              {t('settings.twoFactor.cancel')}
            </Button>
          </div>
        </Stack>
      </form>
    );
  } else if (mode === 'disable') {
    content = (
      <form onSubmit={(e) => void onDisable(e)} noValidate>
        <Stack gap="4">
          <Heading level={3}>{t('settings.twoFactor.disable.title')}</Heading>
          <Text size="sm">{t('settings.twoFactor.disable.intro')}</Text>
          <Input
            id="two-factor-disable-password"
            type="password"
            autoComplete="current-password"
            label={t('settings.twoFactor.disable.passwordLabel')}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordError(null);
            }}
            error={passwordError ?? undefined}
            disabled={busy}
          />
          <Input
            id="two-factor-disable-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            label={t('settings.twoFactor.codeLabel')}
            placeholder="123456"
            value={code}
            onChange={(e) => {
              setCode(sixDigits(e.target.value));
              setCodeError(null);
            }}
            error={codeError ?? undefined}
            disabled={busy}
          />
          {failureAlert}
          <div className="two-factor__actions">
            <Button type="submit" variant="danger" isLoading={busy} disabled={busy}>
              {t('settings.twoFactor.disable.confirmButton')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => reset('idle')} disabled={busy}>
              {t('settings.twoFactor.cancel')}
            </Button>
          </div>
        </Stack>
      </form>
    );
  } else {
    const enabled = status.data.enabled;
    content = (
      <Stack gap="4">
        <div className="two-factor__status">
          <Badge tone={enabled ? 'success' : 'neutral'}>
            {enabled ? t('settings.twoFactor.statusOnBadge') : t('settings.twoFactor.statusOffBadge')}
          </Badge>
          <Text size="sm">{enabled ? t('settings.twoFactor.statusOn') : t('settings.twoFactor.statusOff')}</Text>
        </div>
        {failureAlert}
        <div className="two-factor__actions">
          {enabled ? (
            <Button variant="secondary" onClick={() => { setNotice(null); reset('disable'); }}>
              {t('settings.twoFactor.disable.button')}
            </Button>
          ) : (
            <Button variant="primary" onClick={() => void beginSetup()} isLoading={busy} disabled={busy}>
              {t('settings.twoFactor.enableButton')}
            </Button>
          )}
        </div>
        {enabled && <Text size="sm" color="muted">{t('settings.twoFactor.lostDevice')}</Text>}
      </Stack>
    );
  }

  return (
    <Section
      title={t('settings.twoFactor.title')}
      description={t('settings.twoFactor.description')}
      className={className || undefined}
    >
      {content}
      <LiveRegion>
        {notice ? (
          <Alert tone="success" live="off" compact>
            {t(notice === 'enabled' ? 'settings.twoFactor.enabled' : 'settings.twoFactor.disabled')}
          </Alert>
        ) : null}
      </LiveRegion>
    </Section>
  );
}

export default TwoFactorSection;
